import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function Landing() {
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accountNote, setAccountNote] = useState<string | null>(null);
  const [cfg, setCfg] = useState<{
    cnpj: string | null; responsible_name: string; responsible_role: string;
    lgpd_text: string | null; logo_path: string | null; company_legal_name: string;
  } | null>(null);

  useEffect(() => {
    fetch('/api/settings/public')
      .then((r) => r.json())
      .then((d) => setCfg((d as { settings: null }).settings ?? null))
      .catch(() => setCfg(null));
  }, []);
  const [recap, setRecap] = useState<{ company: string; email: string; conta: boolean } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  function clearField(name: string) {
    setFieldErrors((fe) => { const n = { ...fe }; delete n[name]; return n; });
  }

  function maskCnpj(v: string): string {
    const d = v.replace(/\D/g, '').slice(0, 14);
    if (d.length <= 2) return d;
    if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
    if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
    if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const fd = new FormData(e.currentTarget);
    try {
      const res = await fetch('/api/landing/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: fd.get('companyName'),
          contactName: fd.get('contactName'),
          email: fd.get('email'),
          phone: fd.get('phone'),
          cnpj: fd.get('cnpj'),
          linesEstimate: String(fd.get('linesEstimate') || '').trim() || undefined,
          monthlySpendEstimate: String(fd.get('monthlySpendEstimate') || '').trim() || undefined,
          message: fd.get('message') || undefined,
          org_name: fd.get('org_name'),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const details = (data as { details?: Record<string, string[]> }).details;
        if (details) {
          const fe: Record<string, string> = {};
          for (const [k, msgs] of Object.entries(details)) {
            if (msgs?.length) fe[k] = msgs[0];
          }
          setFieldErrors(fe);
        }
        setError((data as { error?: string }).error ?? 'Não foi possível enviar. Revise os campos destacados e tente de novo.');
        requestAnimationFrame(() => {
          document.querySelector<HTMLInputElement>('[aria-invalid="true"]')?.focus();
        });
        return;
      }
      const { id } = (await res.json()) as { id: string };
      const accountFile = fd.get('account') as File | null;
      const company = String(fd.get('companyName') ?? '');
      const email = String(fd.get('email') ?? '');
      const upOk = accountFile && accountFile.size > 0
        ? await (async () => {
            const up = new FormData();
            up.append('account', accountFile, accountFile.name);
            const upRes = await fetch('/api/landing/leads/' + id + '/upload', { method: 'POST', body: up });
            if (!upRes.ok) {
              const upData = await upRes.json().catch(() => ({}));
              setAccountNote((upData as { error?: string }).error ?? 'Não conseguimos anexar o arquivo; a conta vem no contato.');
              return false;
            }
            return true;
          })()
        : null;
      setRecap({ company, email, conta: upOk === true });
      setSent(true);
    } catch {
      setError('Falha de conexão. Tente novamente.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="min-h-screen bg-white">
      <header className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
        <div className="flex items-center gap-2.5">
          {cfg?.logo_path ? (
            <img src={cfg.logo_path} alt="Logo Meta Telecom" className="h-7 w-auto rounded-md" />
          ) : (
            <span aria-hidden="true" className="grid h-7 w-7 place-items-center rounded-md bg-red-claro text-[11px] font-bold text-white">MT</span>
          )}
          <span className="text-sm font-bold tracking-tight">Meta Telecom</span>
          <span className="hidden rounded-full border border-[var(--border-strong)] px-2.5 py-1 text-[11px] font-medium text-[var(--text-dim)] sm:block">
            Parceiro autorizado Claro Empresas
          </span>
        </div>
        <Link to="/painel/login" className="inline-flex min-h-[44px] items-center rounded-md px-2 py-2 text-sm font-medium text-[var(--text-dim)] transition-colors hover:text-[var(--text)]">
          Área do time
        </Link>
      </header>

      <section className="mx-auto max-w-6xl px-6 pt-16 pb-20 md:pt-24">
        <p className="ce-fade text-[13px] font-semibold uppercase tracking-widest text-[var(--text-dim)]">
          Telefonia corporativa · Migração assistida
        </p>
        <h1 className="ce-fade ce-fade-1 mt-4 max-w-3xl text-[clamp(2rem,5vw,3.4rem)] font-bold leading-[1.08] tracking-tight">
          Descubra em até 24h úteis quanto a sua empresa pode economizar na telefonia.
        </h1>
        <p className="ce-fade ce-fade-2 mt-5 max-w-xl text-lg leading-relaxed text-[var(--text-dim)]">
          Envie a conta atual (em PDF ou print). Nosso time analisa linha a linha, em sigilo,
          e te apresenta as melhores condições dos planos Claro Empresas. Sem custo. Sem compromisso.
        </p>
        <div className="ce-fade ce-fade-3 mt-8 flex flex-wrap items-center gap-4">
          <a href="#analise"
            className="inline-flex h-12 items-center rounded-lg bg-red-claro px-6 text-base font-semibold text-white transition-colors hover:bg-[#c40a20]">
            Pedir análise gratuita
          </a>
          <span className="text-sm text-[var(--text-dim)]">Resposta em até 24h úteis · Sem compromisso</span>
        </div>
      </section>

      <section className="border-y border-[var(--border)] bg-[var(--bg-subtle)]">
        <div className="mx-auto grid max-w-6xl gap-8 px-6 py-14 md:grid-cols-3">
          {[
            { n: '1', t: 'Você manda a conta atual', d: 'PDF da fatura ou print. Leva 1 minuto. Não precisa de acesso a nada.' },
            { n: '2', t: 'Analisamos em sigilo', d: 'Nossa equipe cruza o consumo da sua conta com os planos Claro Empresas do momento. A análise fica só entre nós: nada disso aparece no site.' },
            { n: '3', t: 'Você recebe o contato', d: 'Em até 24h úteis, apresentamos as condições do melhor cenário para a sua empresa. Se não valer a pena, dizemos honestamente.' },
          ].map((s) => (
            <div key={s.n}>
              <span className="text-2xl font-bold tabular-nums text-[var(--text-dim)]">{s.n}</span>
              <h2 className="mt-2 text-base font-semibold tracking-tight">{s.t}</h2>
              <p className="mt-1 text-sm leading-relaxed text-[var(--text-dim)]">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="analise" className="mx-auto max-w-2xl px-6 py-20">
        <span className="sr-only" role="status" aria-live="polite">
          {sent ? 'Pedido enviado com sucesso. Nossa equipe entra em contato em até 24h úteis.' : ''}
        </span>
        {sent && recap ? (
          <div id="enviado" role="status" aria-live="polite" className="rounded-xl border border-[var(--border)] bg-white p-8 text-center">
            <h2 className="text-xl font-semibold tracking-tight">Pedido recebido. Vamos cuidar disso.</h2>
            <p className="mt-3 text-sm text-[var(--text-dim)]">
              Registramos a análise para <strong className="text-[var(--text)]">{recap.company}</strong>{' '}
              no email <strong className="text-[var(--text)]">{recap.email}</strong>.
              {accountNote ? ' A conta não pôde ser anexada agora; nossa equipe pede no contato. (' + accountNote + ')'
              : recap.conta ? ' Sua conta já está com o pedido.'
              : ' Quando puder, envie a conta (PDF ou print) que nossa equipe vai pedir no contato.'}
            </p>
            <p className="mt-2 text-sm text-[var(--text)]">Resposta em até 24h úteis.</p>
            <p className="mt-4 rounded-lg bg-[var(--bg-subtle)] p-3 text-xs text-[var(--text-dim)]">
              O contato vem do email do time Meta Telecom; se não aparecer em pouco tempo, procure também na caixa de spam.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-5 flex items-center gap-3 rounded-lg bg-[var(--bg-subtle)] p-3">
              <span aria-hidden="true" className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-zinc-900 text-xs font-bold text-white">IB</span>
              <p className="text-xs leading-snug text-[var(--text-dim)]">
                <strong className="font-semibold text-[var(--text)]">Quem fala com você:</strong> {cfg ? `${cfg.responsible_name}, ${cfg.responsible_role.toLowerCase()} da ${cfg.company_legal_name}` : 'Igor Bessa, sócio da Meta Telecom'}. Quem responde o seu form é pessoa, com nome, respondendo por tudo que promete aqui.
              </p>
            </div>
            <h2 className="text-2xl font-semibold tracking-tight">Peça a análise gratuita</h2>
            <p className="mt-1 text-sm text-[var(--text-dim)]">Leva menos de 2 minutos. Só usamos os dados para preparar a sua análise.</p>
            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Empresa" required err={fieldErrors.companyName} name="companyName">
                  <input name="companyName" required aria-invalid={!!fieldErrors.companyName} aria-describedby={fieldErrors.companyName ? 'err-companyName' : undefined} onInput={() => clearField('companyName')} className="field" autoComplete="organization" />
                </Field>
                <Field label="Responsável" err={fieldErrors.contactName} name="contactName">
                  <input name="contactName" aria-invalid={!!fieldErrors.contactName} aria-describedby={fieldErrors.contactName ? 'err-contactName' : undefined} onInput={() => clearField('contactName')} className="field" autoComplete="name" />
                </Field>
              </div>
              <label className="block">
                <span className="mb-1 block text-sm font-medium">CNPJ (opcional)</span>
                <input name="cnpj" inputMode="numeric" placeholder="00.000.000/0000-00" autoComplete="off"
                  onInput={(e) => { e.currentTarget.value = maskCnpj(e.currentTarget.value); }} className="field" />
              </label>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Email" required err={fieldErrors.email} name="email">
                  <input name="email" type="email" required autoComplete="email" aria-invalid={!!fieldErrors.email} aria-describedby={fieldErrors.email ? 'err-email' : undefined} onInput={() => clearField('email')} className="field" />
                </Field>
                <Field label="WhatsApp / telefone" err={fieldErrors.phone} name="phone" hint="Prefere WhatsApp? Deixe o número que você atende: o contato em 24h pode ser por aí.">
                  <input name="phone" type="tel" autoComplete="tel" placeholder="(21) 99999-9999" aria-invalid={!!fieldErrors.phone} aria-describedby={fieldErrors.phone ? 'err-phone' : undefined} onInput={() => clearField('phone')} className="field" />
                </Field>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Aprox. nº de linhas" err={fieldErrors.linesEstimate} name="linesEstimate" hint="Quantas linhas de celular/telefone a empresa tem hoje, aproximadamente.">
                  <input name="linesEstimate" inputMode="numeric" placeholder="Ex: 12" autoComplete="off" aria-invalid={!!fieldErrors.linesEstimate} aria-describedby={fieldErrors.linesEstimate ? 'err-linesEstimate' : undefined} onInput={() => clearField('linesEstimate')} className="field" />
                </Field>
                <Field label="Gasto mensal com telefonia (R$)" err={fieldErrors.monthlySpendEstimate} name="monthlySpendEstimate" hint="A fatura total, se souber de cabeça. Não precisa abrir a conta agora.">
                  <input name="monthlySpendEstimate" inputMode="decimal" placeholder="Ex: 1.500" autoComplete="off" aria-invalid={!!fieldErrors.monthlySpendEstimate} aria-describedby={fieldErrors.monthlySpendEstimate ? 'err-monthlySpendEstimate' : undefined} onInput={() => clearField('monthlySpendEstimate')} className="field" />
                </Field>
              </div>
              <label className="block">
                <span className="mb-1 block text-sm font-medium">Algo que queira resolver? (opcional)</span>
                <textarea name="message" rows={3} placeholder="Ex: contrato vence em dezembro, quero reduzir custo por linha"
                  className="mt-0 w-full rounded-lg border border-[var(--border-strong)] bg-white p-3 text-sm" />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium">Conta atual (PDF ou print, opcional) e agiliza seu atendimento</span>
                <input name="account" type="file" accept=".pdf,.png,.jpg,.jpeg"
                  className="mt-1 block w-full text-sm text-[var(--text-dim)] file:mr-3 file:h-10 file:cursor-pointer file:rounded-lg file:border-0 file:bg-[var(--bg-subtle)] file:px-4 file:text-sm file:font-medium file:text-[var(--text)] hover:file:bg-[var(--border)]" />
                <span className="mt-1 block text-xs text-[var(--text-dim)]">Se der trabalho achar agora, sem problema: pedimos no contato.</span>
              </label>
              {/* honeypot anti-bot: invisível para pessoas (nome genérico para não atrair autofill) */}
              <input name="org_name" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
              {error && <p role="alert" className="text-sm text-red-claro">{error}</p>}
              <button type="submit" disabled={busy}
                className="h-12 w-full rounded-lg bg-red-claro text-base font-semibold text-white transition-colors hover:bg-[#c40a20] disabled:opacity-50 sm:w-auto sm:px-8">
                {busy ? 'Enviando...' : 'Quero minha análise'}
              </button>
              <p className="text-xs text-[var(--text-dim)]">Ao enviar, você concorda em receber contato sobre esta análise. {cfg?.lgpd_text || 'Seus dados são usados apenas para isto (LGPD) e nunca compartilhados.'}{' '}
  <Link to="/privacidade" className="underline underline-offset-2 hover:text-[var(--text)]">Política de privacidade</Link></p>
            </form>
          </>
        )}
      </section>
      <footer className="border-t border-[var(--border)]">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-6 py-6 text-xs text-[var(--text-dim)]">
          <span>Meta Telecom · Parceiro autorizado Claro Empresas{cfg?.cnpj ? ` · CNPJ ${cfg.cnpj}` : ''}</span>
          <span className="flex flex-wrap gap-x-4 gap-y-1">
            <Link to="/privacidade" className="underline underline-offset-2 hover:text-[var(--text)]">Política de privacidade</Link>
            <span>Claro e o logo Claro são marcas da Claro S/A; este site pertence a um parceiro de revenda.</span>
          </span>
        </div>
      </footer>
    </main>
  );
}

function Field({
  label, required, children, hint, err, name,
}: {
  label: string; required?: boolean; children: React.ReactNode; hint?: string | null; err?: string; name: string;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium">
        {label}{required && ' *'}
      </span>
      {children}
      {err ? <span id={`err-${name}`} className="mt-1 block text-xs text-red-claro">{err}</span> : hint ? <span className="mt-1 block text-xs text-[var(--text-dim)]">{hint}</span> : null}
    </label>
  );
}
